import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Search, Eye, CheckCircle2, AlertTriangle, XCircle, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const RIESGO_COLORS = { BAJO: 'bg-green-100 text-green-700', MEDIO: 'bg-yellow-100 text-yellow-700', ALTO: 'bg-orange-100 text-orange-700', CRITICO: 'bg-red-100 text-red-700' };
const SEMAFORO_CONFIG = { OK: { color: 'text-emerald-600', icon: CheckCircle2 }, ALERTA: { color: 'text-amber-500', icon: AlertTriangle }, CRITICO: { color: 'text-red-600', icon: XCircle } };
const ESTADO_COLORS = { EN_VALIDACION: 'bg-blue-50 text-blue-700 border border-blue-200', PEND_APROBACION: 'bg-purple-50 text-purple-700 border border-purple-200', APROBADA: 'bg-emerald-50 text-emerald-700 border border-emerald-200', RECHAZADA: 'bg-red-50 text-red-700 border border-red-200' };
const ESTADO_LABELS = { EN_VALIDACION: 'EN VALIDACION', PEND_APROBACION: 'PEND. APROBACION', APROBADA: 'APROBADA', RECHAZADA: 'RECHAZADA' };

export default function AprobadorActividades() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };
  const [actividades, setActividades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('PEND_APROBACION');

  const fetchActividades = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filtroEstado) params.set('estado', filtroEstado);
      const res = await axios.get(`${API}/actividades-operacionales?${params}`, { headers });
      setActividades(res.data);
    } catch { }
    setLoading(false);
  }, [token, search, filtroEstado]);

  useEffect(() => { fetchActividades(); }, [fetchActividades]);

  const stats = {
    pendientes: actividades.length,
  };

  return (
    <div className="space-y-6" data-testid="aprobador-actividades">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Actividades por Aprobar</h1>
        <p className="text-sm text-slate-500 mt-0.5">Revise y apruebe o rechace las actividades operacionales</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar actividades..."
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-300"
              data-testid="search-actividades" />
          </div>
          <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600" data-testid="filter-estado">
            <option value="">Todos los estados</option>
            <option value="PEND_APROBACION">Pend. Aprobacion</option>
            <option value="APROBADA">Aprobadas</option>
            <option value="RECHAZADA">Rechazadas</option>
            <option value="EN_VALIDACION">En Validacion</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
        ) : actividades.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <CheckCircle2 className="w-12 h-12 text-slate-300 mb-3" />
            <p className="text-sm text-slate-500 font-medium">No hay actividades {filtroEstado === 'PEND_APROBACION' ? 'pendientes de aprobacion' : 'con este filtro'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="actividades-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Codigo</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Titulo</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Empresa</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Fecha</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Riesgo</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Semaforo</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Estado</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {actividades.map(a => {
                  const sem = SEMAFORO_CONFIG[a.semaforo] || SEMAFORO_CONFIG.OK;
                  const SemIcon = sem.icon;
                  return (
                    <tr key={a.id} className="hover:bg-slate-50/50 transition-colors" data-testid={`actividad-row-${a.id}`}>
                      <td className="px-4 py-3"><span className="text-sm font-mono text-slate-600">{a.codigo}</span></td>
                      <td className="px-4 py-3"><p className="text-sm font-medium text-slate-800 max-w-[280px] truncate">{a.titulo}</p></td>
                      <td className="px-4 py-3"><p className="text-sm text-slate-600">{a.empresa_nombre}</p></td>
                      <td className="px-4 py-3"><p className="text-sm text-slate-600">{a.fecha_planificada.split('-').reverse().join('/')}</p></td>
                      <td className="px-4 py-3 text-center"><span className={`inline-block text-xs font-bold px-2.5 py-1 rounded ${RIESGO_COLORS[a.nivel_riesgo] || ''}`}>{a.nivel_riesgo}</span></td>
                      <td className="px-4 py-3 text-center"><span className={`inline-flex items-center gap-1 text-xs font-medium ${sem.color}`}><SemIcon className="w-3.5 h-3.5" />{a.semaforo}</span></td>
                      <td className="px-4 py-3 text-center"><span className={`inline-block text-[11px] font-medium px-2.5 py-1 rounded ${ESTADO_COLORS[a.estado] || ''}`}>{ESTADO_LABELS[a.estado] || a.estado}</span></td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => navigate(`/aprobador/actividades/${a.id}`)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors" data-testid={`ver-${a.id}`}>
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
